import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabase/config';
import { Building2, Users, ExternalLink, Activity, BarChart3, TrendingUp, BellRing, DollarSign } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const AdminPanel = () => {
    const [tenants, setTenants] = useState([]);
    const [users, setUsers] = useState([]);
    const [conversions, setConversions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('businesses');
    const { switchTenant } = useAuth();
    const navigate = useNavigate();

    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [tRes, uRes, niRes, oRes] = await Promise.all([
                    supabase.from('tenants').select('*'),
                    supabase.from('users').select('*'),
                    supabase.from('notification_interactions').select('*').order('timestamp', { ascending: false }).limit(500),
                    supabase.from('orders').select('*').order('timestamp', { ascending: false }).limit(1000),
                ]);

                if (tRes.error) throw tRes.error;
                if (uRes.error) throw uRes.error;

                const tenantList = tRes.data || [];
                setTenants(tenantList);
                setUsers(uRes.data || []);

                // Conversion Analysis: Match notif interaction with same-day purchase
                const notifInteractions = niRes.data || [];
                const allPurchases = (oRes?.data || []).map(o => ({
                    ...o,
                    timestamp: o.timestamp || o.created_at,
                    customer_id: o.customer_id,
                    tenant_id: o.tenant_id
                }));

                const matchedConversions = [];
                notifInteractions.forEach(ni => {
                    const niDate = new Date(ni.timestamp);

                    // Find interactions by same customer in same business on same day
                    const relatedPurchases = allPurchases.filter(i => {
                        const iDate = new Date(i.timestamp);
                        const isSameDay = iDate.toDateString() === niDate.toDateString();
                        const isAfterNotif = iDate >= niDate;
                        return i.customer_id === ni.customer_id && i.tenant_id === ni.tenant_id && isSameDay && isAfterNotif;
                    });

                    if (relatedPurchases.length > 0) {
                        const tenant = tenantList.find(t => t.id === ni.tenant_id);
                        matchedConversions.push({
                            id: ni.id,
                            businessName: tenant?.name || 'Unknown',
                            customerId: ni.customer_id,
                            timestamp: niDate,
                            purchaseCount: relatedPurchases.length,
                            purchases: relatedPurchases
                        });
                    }
                });

                setConversions(matchedConversions);
            } catch (err) {
                console.error("AdminPanel fetch error:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (error) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
                <h2>Veri yüklenirken hata oluştu</h2>
                <p>{error}</p>
                <button onClick={() => window.location.reload()} style={{ marginTop: '1rem' }}>Tekrar Dene</button>
            </div>
        );
    }

    const stats = [
        { label: 'Total Businesses', value: tenants.length, icon: Building2, color: '#0ea5e9' },
        { label: 'Total System Users', value: users.length, icon: Users, color: '#2dd4bf' },
        { label: 'Successful Conversions', value: conversions.length, icon: TrendingUp, color: '#f59e0b' },
        { label: 'Platform Status', value: 'Operational', icon: Activity, color: '#22c55e' }
    ];

    const handleConnect = (tenantId) => {
        switchTenant(tenantId);
        navigate('/app');
    };

    const handleTogglePlan = async (tenant) => {
        const cycle = { free: 'pro', pro: 'premium', premium: 'free' };
        const newPlan = cycle[tenant.plan] || 'pro';
        const { error } = await supabase
            .from('tenants')
            .update({ plan: newPlan })
            .eq('id', tenant.id);
        if (!error) {
            setTenants(prev => prev.map(t => t.id === tenant.id ? { ...t, plan: newPlan } : t));
        }
    };

    return (
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ marginBottom: '3rem' }}>
                <h1 style={{
                    fontSize: '2.25rem',
                    fontWeight: 800,
                    marginBottom: '0.5rem',
                    background: 'linear-gradient(to right, var(--primary), var(--accent))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }}>
                    Global System Dashboard
                </h1>
                <p style={{ color: 'var(--text-muted)' }}>Manage all registered businesses and monitor system health.</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem', marginBottom: '3rem' }}>
                {stats.map((stat, idx) => (
                    <div key={idx} className="card glass" style={{ padding: '1.5rem', border: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem' }}>
                            <div style={{ padding: '10px', borderRadius: '12px', background: `${stat.color}15`, color: stat.color }}>
                                <stat.icon size={20} />
                            </div>
                            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-muted)' }}>{stat.label}</span>
                        </div>
                        <div style={{ fontSize: '1.75rem', fontWeight: 800 }}>{stat.value}</div>
                    </div>
                ))}
            </div>

            {/* Tabs */}
            <div style={{
                display: 'flex',
                gap: '0.5rem',
                marginBottom: '2.5rem',
                background: 'rgba(var(--primary-rgb), 0.05)',
                padding: '0.5rem',
                borderRadius: '16px',
                width: 'fit-content',
                border: '1px solid var(--border)',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch'
            }}>
                <button
                    onClick={() => setActiveTab('businesses')}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 24px',
                        borderRadius: '12px',
                        border: 'none',
                        background: activeTab === 'businesses' ? 'var(--primary)' : 'transparent',
                        color: activeTab === 'businesses' ? 'var(--text-on-primary)' : 'var(--text-main)',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                >
                    <Building2 size={18} /> Businesses
                </button>
                <button
                    onClick={() => setActiveTab('conversions')}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 24px',
                        borderRadius: '12px',
                        border: 'none',
                        background: activeTab === 'conversions' ? 'var(--primary)' : 'transparent',
                        color: activeTab === 'conversions' ? 'var(--text-on-primary)' : 'var(--text-main)',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                >
                    <TrendingUp size={18} /> Bildirimden Satışa
                </button>
            </div>

            {activeTab === 'businesses' ? (
                <div className="card glass" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--border)' }}>
                    <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 style={{ fontSize: '1.125rem', fontWeight: 700 }}>Registered Businesses</h2>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(var(--primary-rgb), 0.02)' }}>
                                    <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Company Instance</th>
                                    <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Industry</th>
                                    <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Plan</th>
                                    <th style={{ padding: '1rem 1.5rem', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', textAlign: 'right' }}>Management</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="3" style={{ padding: '3rem', textAlign: 'center' }}>Synchronizing data...</td></tr>
                                ) : tenants.map((tenant) => (
                                    <tr key={tenant.id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.2s' }} className="table-row-hover">
                                        <td style={{ padding: '1rem 1.5rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div className="logo-icon" style={{
                                                    width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    borderRadius: '10px', background: '#6f4e3710',
                                                    color: '#6f4e37', border: '1px solid var(--border)'
                                                }}>
                                                    {tenant.theme?.logoUrl ? (
                                                        <img src={tenant.theme.logoUrl} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }} alt="" />
                                                    ) : <Building2 size={20} />}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: '0.9375rem' }}>{tenant.name}</div>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>UID: {tenant.id}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem' }}>
                                            <span style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, background: 'var(--bg-main)', border: '1px solid var(--border)' }}>
                                                {tenant.industry || 'General'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem' }}>
                                            <button
                                                onClick={() => handleTogglePlan(tenant)}
                                                title={`Tıkla: Plan değiştir (free → pro → premium → free)`}
                                                style={{
                                                    padding: '4px 10px',
                                                    borderRadius: '6px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 800,
                                                    background: tenant.plan === 'premium' ? 'rgba(192, 38, 211, 0.1)' : tenant.plan === 'pro' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(var(--primary-rgb), 0.1)',
                                                    color: tenant.plan === 'premium' ? '#c026d3' : tenant.plan === 'pro' ? '#10b981' : 'var(--primary)',
                                                    textTransform: 'uppercase',
                                                    border: '1px dashed',
                                                    borderColor: tenant.plan === 'premium' ? '#c026d3' : tenant.plan === 'pro' ? '#10b981' : 'var(--primary)',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {tenant.plan || 'free'}
                                            </button>
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                                            <button onClick={() => handleConnect(tenant.id)} style={{ fontSize: '0.8125rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                                                <ExternalLink size={14} /> Impersonate
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="card glass" style={{ padding: '2rem', border: '1px solid var(--border)' }}>
                    <div style={{ marginBottom: '2rem' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.5rem' }}>Bildirimden Satışa Dönüşüm Analizi</h2>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Aşağıdaki tablo, bir push bildirimine tıklayıp aynı gün içerisinde harcama yapan müşterileri listeler.</p>
                    </div>

                    {conversions.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>
                            <BellRing size={48} style={{ margin: '0 auto 1.5rem', opacity: 0.2 }} />
                            <p>Henüz bir dönüşüm kaydı bulunamadı.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gap: '1rem' }}>
                            {conversions.map((conv) => (
                                <div key={conv.id} style={{
                                    padding: '1.5rem',
                                    background: 'rgba(var(--primary-rgb), 0.02)',
                                    borderRadius: '16px',
                                    border: '1px solid var(--border)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                                        <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <TrendingUp size={24} />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 800, fontSize: '1.125rem' }}>{conv.businessName}</div>
                                            <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                                                Müşteri ID: {conv.customerId.slice(-6)} • {conv.timestamp.toLocaleString()}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#10b981', fontWeight: 800, fontSize: '1.25rem' }}>
                                            <TrendingUp size={20} />
                                            {conv.purchaseCount} İşlem
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Bildirim sonrası aynı gün harcama</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminPanel;
